import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { getProduct, money } from "../lib/products";
import { useCart } from "../context/CartContext";
import { QtyStepper } from "../components/site/CartDrawer";
import { NewsletterForm } from "../components/site/NewsletterForm";
import { ComingSoon, EASE, Eyebrow, MaskLine, Reveal } from "../components/site/motion";

const Nutrition = ({ p }) => (
  <div data-testid="nutrition-placeholder" className="rounded-[24px] border-2 border-dano-ink bg-white/50 p-6">
    <div className="flex items-baseline justify-between border-b-4 border-dano-ink pb-2">
      <h3 className="font-wide text-lg sm:text-xl uppercase">Nutrition facts</h3>
      <span className="font-cond text-sm uppercase tracking-[0.18em] text-dano-red whitespace-nowrap">Coming soon</span>
    </div>
    <ul className="divide-y divide-dano-ink/20">
      <li className="flex justify-between py-2 text-sm"><span>Bar size</span><span className="font-semibold">{p.size}</span></li>
      <li className="flex justify-between py-2 text-sm"><span>Protein</span><span className="font-semibold">{p.protein}</span></li>
    </ul>
    <p className="mt-3 text-sm text-dano-ink/80" data-testid="nutrition-coming-soon">{p.nutrition}</p>
  </div>
);

const NotifyBlock = ({ p }) => (
  <div className="mt-8 rounded-[24px] bg-dano-sand/80 p-6" data-testid="notify-block">
    <p className="font-cond text-sm uppercase tracking-[0.18em] text-dano-ink/80">Price</p>
    <p className="mt-1 font-wide text-3xl uppercase" data-testid="product-price">Price coming soon</p>
    <p className="mt-4 mb-3 text-sm text-dano-ink/80">Get an email when {p.name} launches.</p>
    <NewsletterForm source={`notify-${p.slug}`} id="notify" label="Notify me at launch" successMsg={`We'll email you when ${p.name} launches.`} />
  </div>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const p = getProduct(slug);
  const { add } = useCart();
  const [variant, setVariant] = useState(p?.variants?.[0]);
  const [qty, setQty] = useState(1);

  if (!p) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-40 pb-32 text-center" data-testid="product-not-found">
        <h1 className="font-wide text-4xl uppercase">Bar not found</h1>
        <Link to="/shop" className="mt-6 inline-block text-dano-red underline">Back to shop</Link>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page" className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pt-28 sm:pt-32 pb-24">
      <Link to="/shop" data-testid="back-to-shop-link" className="inline-flex items-center gap-2 font-cond uppercase tracking-widest text-dano-ink/80 hover:text-dano-red">
        <ArrowLeft className="h-4 w-4" /> Shop
      </Link>
      <div className="mt-6 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <div className="lg:sticky lg:top-28">
            <motion.div
              initial={{ clipPath: "inset(0 0 100% 0 round 28px)" }}
              animate={{ clipPath: "inset(0 0 0% 0 round 28px)" }}
              transition={{ duration: 1.2, ease: EASE }}
              className="spotlight-frame relative aspect-square overflow-hidden bg-dano-ink"
            >
              <img src={p.image} alt={p.name} className="h-full w-full object-cover" data-testid="product-image" />
              <div className="absolute left-5 top-5"><ComingSoon testId="product-coming-soon-badge" /></div>
              <span className="absolute bottom-5 left-5 rounded-full bg-dano-cream/90 px-3 py-1 text-xs text-dano-ink">Sample image, not final product</span>
            </motion.div>
          </div>
        </div>
        <div className="min-w-0 lg:col-span-5 lg:col-start-8">
          <Eyebrow>{p.number} · Coming soon</Eyebrow>
          <h1 className="mt-4 font-wide text-5xl sm:text-6xl uppercase leading-[0.88]" data-testid="product-name">
            {p.name.split(" ").map((w, i, a) => (
              <MaskLine key={w} i={i} className={i === a.length - 1 ? "text-dano-red" : ""}>{w}</MaskLine>
            ))}
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-dano-ink/80">{p.description}</p>

          {p.variants?.length ? (<>
          <div className="mt-8 grid grid-cols-2 gap-3" role="radiogroup" aria-label="Pack size">
            {p.variants.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setVariant(v)}
                data-testid={`variant-option-${v.id}`}
                aria-pressed={variant?.id === v.id}
                className={`btn-press rounded-2xl border-2 p-4 text-left transition-colors duration-300 ${variant?.id === v.id ? "border-dano-red bg-dano-red text-dano-cream" : "border-dano-ink hover:bg-dano-sand"}`}
              >
                <span className="block font-cond text-sm uppercase tracking-[0.18em]">{v.label}</span>
                <span className="block font-wide text-2xl">{money(v.price)}</span>
              </button>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <QtyStepper value={qty} onChange={(q) => setQty(Math.max(1, Math.min(99, q)))} testId="product-qty" />
            <button
              onClick={() => add(p, variant, qty)}
              data-testid="add-to-cart-button"
              className="btn-press flex-1 rounded-full bg-dano-ink px-8 py-4 font-cond text-lg uppercase tracking-widest text-dano-cream hover:bg-dano-red"
            >
              Add to cart · {money((variant?.price ?? 0) * qty)}
            </button>
          </div>
          <p className="mt-3 text-sm text-dano-ink/70">Coming soon: orders placed now are test orders only. No payment is taken.</p>
          </>) : <NotifyBlock p={p} />}

          <Reveal className="mt-12">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-[24px] bg-dano-gold p-6">
                <p className="font-cond text-sm uppercase tracking-[0.18em]">Protein</p>
                <p className="mt-1 font-wide text-4xl" data-testid="product-protein">{p.protein}</p>
                <p className="mt-1 text-xs">Approximately, per bar</p>
              </div>
              <div className="rounded-[24px] bg-dano-sand p-6">
                <p className="font-cond text-sm uppercase tracking-[0.18em]">Bar size</p>
                <p className="mt-1 font-wide text-4xl" data-testid="product-size">{p.size}</p>
                <p className="mt-1 text-xs">One substantial bar</p>
              </div>
            </div>
          </Reveal>

          <Reveal className="mt-10">
            <h3 className="font-wide text-xl uppercase">Ingredients</h3>
            <p data-testid="product-ingredients" className="mt-3 text-base text-dano-ink/80">Made with real, recognizable ingredients. Full ingredient list and allergen info coming soon.</p>
          </Reveal>

          <Reveal className="mt-10"><Nutrition p={p} /></Reveal>
        </div>
      </div>
    </div>
  );
}
